import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createLabel, deleteLabel, updateLabel } from '../../../api';
import { requireSuccess } from '../lib/requireSuccess';
import type { LabelMeta, LabelsById } from './queries';
import { labelKeys, normalizeLabel } from './queries';

/** The label a create returned; the response also lists every label, so find it by name as a fallback. */
export const createdLabel = (res: any, name: string): LabelMeta | null =>
    normalizeLabel(res?.label) ??
    normalizeLabel((res?.labels || []).find((label: any) => normalizeLabel(label)?.name === name));

/** Creates a label and adds it to the cached list. */
export const useCreateLabel = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ name, color }: { name: string; color?: string }) => {
            const label = createdLabel(requireSuccess(await createLabel(name, color ?? '')), name);
            if (!label) throw new Error('createLabel returned no label');
            return label;
        },
        onSuccess: (label) => {
            queryClient.setQueryData<LabelsById>(labelKeys.all, (prev) => ({ ...prev, [label.id]: label }));
            queryClient.invalidateQueries({ queryKey: labelKeys.all });
        },
    });
};

type LabelUpdate = { name: string; color?: string } | { name?: string; color: string };

/** Renames or recolours a label; every chip shows the change at once and reverts if the save fails. */
export const useUpdateLabel = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, update }: { id: string; update: LabelUpdate }) =>
            requireSuccess(await updateLabel(id, update)),
        onMutate: async ({ id, update }) => {
            await queryClient.cancelQueries({ queryKey: labelKeys.all });
            const previous = queryClient.getQueryData<LabelsById>(labelKeys.all);
            queryClient.setQueryData<LabelsById>(labelKeys.all, (prev) =>
                prev?.[id] ? { ...prev, [id]: { ...prev[id], ...update } } : prev
            );
            return { previous };
        },
        onError: (_error, _variables, context) => {
            queryClient.setQueryData(labelKeys.all, context?.previous);
        },
        onSettled: () => queryClient.invalidateQueries({ queryKey: labelKeys.all }),
    });
};

/** Deletes a label. Records keep its id, but chips only show ids the list still has. */
export const useDeleteLabel = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => requireSuccess(await deleteLabel(id)),
        onSuccess: (_res, id) => {
            queryClient.setQueryData<LabelsById>(labelKeys.all, (prev) => {
                if (!prev) return prev;
                const next = { ...prev };
                delete next[id];
                return next;
            });
            queryClient.invalidateQueries({ queryKey: labelKeys.all });
        },
    });
};
